import React from "react";
import { Box, TextField, IconButton, Card, CardContent, Typography } from "@mui/material";
import { Delete as DeleteIcon, Add as AddIcon } from "@mui/icons-material";
import {
  OpinionPattern,
  OPINION_PATTERN_EXAMPLE_MAX_CHAR_COUNT,
  OPINION_PATTERN_LABEL_RECOMMENDED_CHAR_COUNT,
  OPINION_PATTERN_DESCRIPTION_MAX_CHAR_COUNT,
} from "../../types";

interface OpinionPatternFormProps {
  patterns: OpinionPattern[];
  onChange: (patterns: OpinionPattern[]) => void;
  difficulty: number;
  error?: string;
}

const OpinionPatternForm: React.FC<OpinionPatternFormProps> = ({
  patterns,
  onChange,
  difficulty,
  error,
}) => {
  // パターン追加
  const addPattern = () => {
    const newPattern: OpinionPattern = {
      typeLabel: "",
      example: "",
      description: "",
    };
    onChange([...patterns, newPattern]);
  };

  // パターン削除
  const removePattern = (index: number) => {
    const newPatterns = patterns.filter((_, i) => i !== index);
    onChange(newPatterns);
  };

  // パターン更新
  const updatePattern = (index: number, field: keyof OpinionPattern, value: string) => {
    const newPatterns = patterns.map((pattern, i) =>
      i === index ? { ...pattern, [field]: value } : pattern
    );
    onChange(newPatterns);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">意見パターン</Typography>
        <IconButton onClick={addPattern} color="primary">
          <AddIcon />
        </IconButton>
      </Box>

      {error && (
        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {patterns.length === 0 && (
        <Typography variant="body2" color="textSecondary" textAlign="center" py={2}>
          意見パターンを追加してください
        </Typography>
      )}

      {patterns.map((pattern, index) => (
        <Card key={index} sx={{ mb: 2 }}>
          <CardContent>
            <Box display="flex" justifyContent="between" alignItems="flex-start" mb={2}>
              <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                パターン {index + 1}
              </Typography>
              <IconButton onClick={() => removePattern(index)} color="error" size="small">
                <DeleteIcon />
              </IconButton>
            </Box>

            <Box sx={{ display: "grid", gap: 2 }}>
              <TextField
                label="パターンラベル"
                value={pattern.typeLabel}
                onChange={(e) => updatePattern(index, "typeLabel", e.target.value)}
                fullWidth
                placeholder="例: 【他との比較】, 【体験から】"
                helperText={`${(pattern.typeLabel || "").length}文字 (推奨: ${OPINION_PATTERN_LABEL_RECOMMENDED_CHAR_COUNT.min}-${OPINION_PATTERN_LABEL_RECOMMENDED_CHAR_COUNT.max}文字)`}
              />

              <TextField
                label="具体例"
                value={pattern.example}
                onChange={(e) => updatePattern(index, "example", e.target.value)}
                multiline
                rows={3}
                fullWidth
                placeholder="このパターンで書かれた具体的な意見文の例を入力..."
                helperText={`${(pattern.example || "").length}文字 (上限: ${OPINION_PATTERN_EXAMPLE_MAX_CHAR_COUNT}文字)`}
              />

              <TextField
                label="説明"
                value={pattern.description}
                onChange={(e) => updatePattern(index, "description", e.target.value)}
                fullWidth
                placeholder="例: → 他の学校や方法と比べて考える"
                helperText={`${(pattern.description || "").length}文字 (上限: ${OPINION_PATTERN_DESCRIPTION_MAX_CHAR_COUNT}文字)`}
              />
            </Box>
          </CardContent>
        </Card>
      ))}

      {patterns.length < 5 && (
        <Box textAlign="center" mt={2}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            推奨: 3-4個のパターンを作成してください
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default OpinionPatternForm;
